// The dream's stand-in cover — Oreia's automatic pick, a piece from the symbol gallery, a photo,
// or Imagine.
//
// A photo the dreamer attached still wins in `entryPreview`. This sheet only chooses the picture
// that stands in until they add one: a catalogue stem stored on `dreamDetails.coverArt`. Clearing
// it is Oreia's pick again, not a blank tile.

import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { IconClose, IconImage, IconSpark } from '../components/Icons';
import type { EntryPreview } from '../entries/preview';
import { artStem, depictingStem, namedPieces } from '../symbols/art';

export interface CoverChooserProps {
  open: boolean;
  onClose: () => void;
  /** The picture Oreia would show with no cover chosen. */
  automatic: EntryPreview;
  /** Catalogue stem currently stored, or undefined for Oreia's pick. */
  chosen?: string;
  /** Names from this dream, so the pieces that actually appear in it sit first. */
  symbols: string[];
  onPick: (stem: string | undefined) => void;
  onUpload: (files: FileList) => void;
  onImagine?: () => void;
  busy?: boolean;
}

function pretty(stem: string): string {
  return stem.replace(/-/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

function depictingFrom(names: string[]): { stem: string; url: string; label: string }[] {
  const seen = new Set<string>();
  const out: { stem: string; url: string; label: string }[] = [];
  for (const raw of names) {
    const label = raw.trim();
    if (!label) continue;
    const stem = artStem(label);
    if (!stem || seen.has(stem)) continue;
    const url = depictingStem(stem);
    if (!url) continue;
    seen.add(stem);
    out.push({ stem, url, label });
  }
  return out;
}

export function CoverChooser({
  open,
  onClose,
  automatic,
  chosen,
  symbols,
  onPick,
  onUpload,
  onImagine,
  busy,
}: CoverChooserProps) {
  useEffect(() => {
    if (!open) return;
    function onKey(event: KeyboardEvent) {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const fromDream = depictingFrom(symbols);
  const used = new Set(fromDream.map((p) => p.stem));
  const gallery = namedPieces().filter((p) => !used.has(p.stem));
  const autoOn = !chosen;

  const pick = (stem: string | undefined) => {
    onPick(stem);
    onClose();
  };

  return createPortal(
    <>
      <div className="orset-scrim" onClick={onClose} />
      <div className="orset ocover" role="dialog" aria-modal="true" aria-label="Choose a picture">
        <div className="orset-head">
          <h2>Choose a picture</h2>
          <button className="orset-x" onClick={onClose} aria-label="Close">
            <IconClose />
          </button>
        </div>

        <div className="orset-body">
          <section className="orset-section">
            <p className="ocover-label">Oreia’s pick</p>
            <button
              className={`ocover-auto${autoOn ? ' on' : ''}`}
              aria-pressed={autoOn}
              onClick={() => pick(undefined)}
            >
              <StandIn preview={automatic} />
              <span>Whatever the dream suggests, until you pick one.</span>
            </button>
          </section>

          {fromDream.length > 0 ? (
            <section className="orset-section">
              <p className="ocover-label">From this dream</p>
              <div className="ocover-grid">
                {fromDream.map((piece) => (
                  <Tile
                    key={piece.stem}
                    url={piece.url}
                    label={piece.label}
                    selected={chosen === piece.stem}
                    onClick={() => pick(piece.stem)}
                  />
                ))}
              </div>
            </section>
          ) : null}

          <section className="orset-section">
            <p className="ocover-label">The gallery</p>
            <div className="ocover-grid">
              {gallery.map((piece) => (
                <Tile
                  key={piece.stem}
                  url={piece.url}
                  label={pretty(piece.stem)}
                  selected={chosen === piece.stem}
                  onClick={() => pick(piece.stem)}
                />
              ))}
            </div>
          </section>

          <div className="ocover-ways">
            <label className="ophoto-add" data-busy={busy || undefined}>
              <IconImage />
              <span>Upload a photo</span>
              <input
                className="ophoto-file"
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => {
                  if (e.target.files?.length) {
                    onUpload(e.target.files);
                    onClose();
                  }
                  e.target.value = '';
                }}
              />
            </label>
            {onImagine ? (
              <button className="ophoto-add" onClick={onImagine}>
                <IconSpark />
                <span>Imagine this dream</span>
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}

function StandIn({ preview }: { preview: EntryPreview }) {
  if (preview.kind === 'gradient') {
    return <span className="ocover-auto-art" style={{ background: preview.background }} />;
  }
  return <img className="ocover-auto-art" src={preview.url} alt="" draggable={false} />;
}

function Tile({
  url,
  label,
  selected,
  onClick,
}: {
  url: string;
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      className={`ocover-tile${selected ? ' on' : ''}`}
      aria-pressed={selected}
      aria-label={label}
      onClick={onClick}
    >
      <img src={url} alt="" draggable={false} />
      <span className="ocover-tile-label">{label}</span>
    </button>
  );
}
